import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const textareaVariants = cva(
  'w-full bg-white border rounded-lg text-secondary placeholder:text-grey-400 focus:outline-none focus:ring-2 transition-all duration-200 disabled:bg-grey-100 disabled:cursor-not-allowed resize-y',
  {
    variants: {
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2.5 text-sm',
        lg: 'px-4 py-3',
      },
      invalid: {
        true: 'border-error focus:border-error focus:ring-error/20',
        false: 'border-grey-200 focus:border-accent focus:ring-accent/20',
      },
    },
    defaultVariants: {
      size: 'md',
      invalid: false,
    },
  },
);

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    Omit<VariantProps<typeof textareaVariants>, 'invalid'> {
  label?: ReactNode;
  error?: string;
  hint?: string;
  /** Extra classes for the textarea element itself (className styles the wrapper, matching Input). */
  textareaClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, textareaClassName, size, label, error, hint, id, rows = 3, ...props }, ref) => {
    const autoId = useId();
    const textareaId = id ?? autoId;
    return (
      <div className={className}>
        {label && (
          <label htmlFor={textareaId} className="label">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          aria-invalid={error ? true : undefined}
          className={cn(textareaVariants({ size, invalid: Boolean(error) }), textareaClassName)}
          {...props}
        />
        {error ? (
          <p className="mt-1 text-caption text-error">{error}</p>
        ) : hint ? (
          <p className="mt-1 text-caption text-grey-500">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
